import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { CredentialResponse } from '@react-oauth/google';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { sequenceWaaS } from '@/config/waas';
import { academyEnrollmentsQueryKey } from '@/hooks/academy/data/useAcademy';
import { waasAuthService } from '@/services/waasAuthService';
import { toConflictAccountType } from '@/components/auth/login/emailConflict';
import type { EmailConflictInfo, SequenceWaaSLoginResult } from '@/types/auth';
import { useApiError } from './useApiError';

const SESSION_NAME = 'Espotz - Web';

/** What a successful login hands back, so the caller can route (profile wizard, return path). */
export type WaaSLoginResult = Pick<
  SequenceWaaSLoginResult,
  'user' | 'isNewUser'
>;

type SignInResponse = Awaited<ReturnType<typeof sequenceWaaS.signIn>>;

/** Thrown when Sequence reports the email belongs to an account of another login type. */
class EmailConflictError extends Error {
  constructor(readonly info: EmailConflictInfo) {
    super('EMAIL_CONFLICT');
  }
}
/** Thrown when a newer sign-in (Resend, Back) replaced this one. */
class SupersededError extends Error {}

/**
 * Headless Sequence WaaS login, the same flow as the mobile app (Espotz-docs#2038): Google (a GIS
 * credential) or email + a 6-digit code, then the Sequence JWT is exchanged for an Espotz session
 * at /auth/waas/login.
 *
 * SDK behaviour this hook has to handle (@0xsequence/waas 2.3):
 * - Every registered `onEmailAuthCodeRequired` / `onEmailConflict` listener is called, and nothing
 *   unregisters them by itself, so listeners are attached for one sign-in only and removed after.
 * - A wrong code keeps the sign-in pending (the user can retry); `submitEmailCode` reports it.
 * - An email conflict also leaves the sign-in pending forever unless it is force-created, so the
 *   sign-in is raced against the conflict and abandoned. `emailConflict` then says which method
 *   the email already uses, so the panel can send the user there. Never force-create: that makes
 *   a second wallet for the same email.
 * - There is no "resend": a new code means a new sign-in for the same email.
 */
export const useWaaSAuth = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [isEmailCodeRequired, setIsEmailCodeRequired] = useState(false);
  const [emailConflict, setEmailConflict] = useState<EmailConflictInfo | null>(
    null,
  );
  const respondWithCodeRef = useRef<((code: string) => Promise<void>) | null>(
    null,
  );
  /** Increments per sign-in; a result for an older attempt is dropped. */
  const attemptRef = useRef(0);
  const cleanupRef = useRef<(() => void) | null>(null);
  const queryClient = useQueryClient();
  const { handleApiError } = useApiError();
  const { t: tAuth } = useTranslation('auth');

  const detach = useCallback(() => {
    cleanupRef.current?.();
    cleanupRef.current = null;
  }, []);

  // Never leave listeners on the SDK singleton after the panel unmounts.
  useEffect(() => detach, [detach]);

  /** Sign in to Sequence, raced against an email conflict, with listeners scoped to this attempt. */
  const signIn = useCallback(
    (
      creds: { idToken: string } | { email: string },
    ): Promise<SignInResponse> => {
      detach();
      setEmailConflict(null);
      const attempt = ++attemptRef.current;
      let rejectConflict: (error: Error) => void = () => undefined;
      const conflict = new Promise<never>((_, reject) => {
        rejectConflict = reject;
      });
      const offCode = sequenceWaaS.onEmailAuthCodeRequired(async respond => {
        if (attempt !== attemptRef.current) return;
        respondWithCodeRef.current = respond;
        setIsEmailCodeRequired(true);
      });
      const offConflict = sequenceWaaS.onEmailConflict(async info => {
        if (attempt !== attemptRef.current) return;
        rejectConflict(
          new EmailConflictError({
            email: info?.email ?? '',
            accountType: toConflictAccountType(info?.type, info?.issuer),
          }),
        );
      });
      cleanupRef.current = () => {
        offCode();
        offConflict();
        rejectConflict(new SupersededError());
      };
      return Promise.race([
        sequenceWaaS.signIn(creds, SESSION_NAME),
        conflict,
      ]).then(response => {
        if (attempt !== attemptRef.current) throw new SupersededError();
        return response;
      });
    },
    [detach],
  );

  /** Exchange the Sequence session for an Espotz one and refresh what depends on the user. */
  const exchange = useCallback(
    async (response: SignInResponse): Promise<WaaSLoginResult> => {
      const { idToken } = await sequenceWaaS.getIdToken();
      if (!idToken)
        throw new Error('Failed to get authentication token from Sequence');
      const login = await waasAuthService.loginWithSequenceToken({
        sequenceToken: idToken,
        walletAddress: response.wallet,
        email: response.email,
      });
      if (login.error || !login.result)
        throw new Error(login.error || 'Authentication failed');

      queryClient.setQueryData(['user'], login.result.user);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['user'] }),
        queryClient.invalidateQueries({ queryKey: ['wallets'] }),
        queryClient.invalidateQueries({ queryKey: academyEnrollmentsQueryKey }),
      ]);
      toast.success(
        login.result.isNewUser
          ? tAuth('waasAuth.success.newAccountCreated')
          : tAuth('waasAuth.success.loginSuccess'),
      );
      return { user: login.result.user, isNewUser: login.result.isNewUser };
    },
    [queryClient, tAuth],
  );

  const fail = useCallback(
    (error: unknown): null => {
      if (error instanceof SupersededError) return null;
      if (error instanceof EmailConflictError) {
        setEmailConflict(error.info);
      } else {
        console.error('WaaS login error:', error);
        handleApiError(error, {
          defaultMessage: tAuth('waasAuth.error.loginFailed'),
        });
      }
      return null;
    },
    [handleApiError, tAuth],
  );

  /** Google: the GIS credential's idToken signs in to Sequence. Null when it didn't complete. */
  const loginWithGoogle = useCallback(
    async (
      credentialResponse: CredentialResponse,
    ): Promise<WaaSLoginResult | null> => {
      if (!credentialResponse.credential) {
        toast.error(tAuth('waasAuth.error.noGoogleToken'));
        return null;
      }
      setIsLoading(true);
      try {
        return await exchange(
          await signIn({ idToken: credentialResponse.credential }),
        );
      } catch (error) {
        return fail(error);
      } finally {
        detach();
        setIsLoading(false);
      }
    },
    [detach, exchange, fail, signIn, tAuth],
  );

  /**
   * Email: sends the code (isEmailCodeRequired turns true), then resolves once the right code is
   * submitted. Calling it again for the same email is "Resend". Null when it didn't complete.
   */
  const loginWithEmail = useCallback(
    async (email: string): Promise<WaaSLoginResult | null> => {
      setIsLoading(true);
      try {
        const pending = signIn({ email });
        // The code step is shown while the sign-in waits; not loading until a code is submitted.
        setIsLoading(false);
        const response = await pending;
        setIsLoading(true);
        return await exchange(response);
      } catch (error) {
        return fail(error);
      } finally {
        detach();
        setIsEmailCodeRequired(false);
        respondWithCodeRef.current = null;
        setIsLoading(false);
      }
    },
    [detach, exchange, fail, signIn],
  );

  /** Submit the 6-digit code. False on a wrong code: the sign-in stays open for another try. */
  const submitEmailCode = useCallback(
    async (code: string): Promise<boolean> => {
      if (!respondWithCodeRef.current || isLoading) return false;
      try {
        setIsLoading(true);
        await respondWithCodeRef.current(code);
        return true;
      } catch {
        setIsLoading(false);
        toast.error(tAuth('waasAuth.error.invalidCode'));
        return false;
      }
    },
    [isLoading, tAuth],
  );

  /** Back from the code step: abandon the pending email sign-in. */
  const cancelEmailLogin = useCallback(() => {
    attemptRef.current++;
    detach();
    respondWithCodeRef.current = null;
    setIsEmailCodeRequired(false);
    setIsLoading(false);
  }, [detach]);

  const clearEmailConflict = useCallback(() => setEmailConflict(null), []);

  return {
    loginWithGoogle,
    loginWithEmail,
    submitEmailCode,
    cancelEmailLogin,
    isLoading,
    isEmailCodeRequired,
    /** Set when the last sign-in hit an email registered with another method. */
    emailConflict,
    clearEmailConflict,
  };
};
